import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ArrowUpRight, Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 pt-16 pb-24" data-testid="forgot-password-page">
      <div className="text-[11px] uppercase tracking-[0.22em] text-muted font-bold">Ministry of Exchange</div>
      <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-ink">Reset your password</h1>

      {sent ? (
        <div data-testid="forgot-password-success" className="mt-8 p-5 rounded-xl border border-ink/10 bg-white">
          <div className="flex items-start gap-3">
            <Mail size={22} className="text-elec shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-ink">Request received.</div>
              <p className="mt-1 text-[13px] text-muted leading-relaxed">
                If <span className="text-ink font-semibold">{email}</span> is on file, a reset link
                is on its way within a few minutes. It expires in 60 minutes and can only be used once.
              </p>
              <p className="mt-3 text-[12px] text-muted leading-relaxed">
                <span className="font-semibold text-ink">Not receiving anything within 5 minutes?</span>{" "}
                Check your spam folder. And double-check you entered the exact email you signed up with.
                A typo means we can't route the reset link to you.{" "}
                <button
                  onClick={() => { setSent(false); }}
                  className="text-elec font-semibold hover:underline"
                  data-testid="forgot-password-resend"
                >
                  Try a different email
                </button>.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-3 text-sm text-muted max-w-sm">
            Enter your account email. We'll send you a secure link to set a new password.
          </p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="moe-field">
              <input
                data-testid="forgot-password-email"
                type="email" required autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder=" "
              />
              <label>Email</label>
            </div>
            {err && <div className="text-sm text-red-600" data-testid="forgot-password-error">{err}</div>}
            <button
              data-testid="forgot-password-submit"
              disabled={busy || !email.trim()}
              className="moe-btn moe-btn-primary w-full disabled:opacity-50"
            >
              {busy ? "Sending…" : <>Send reset link <ArrowUpRight size={15} /></>}
            </button>
          </form>
        </>
      )}

      <div className="mt-8 text-sm text-muted">
        Remembered it? <Link to="/login" className="text-elec font-semibold">Back to log in →</Link>
      </div>
    </div>
  );
}
